/*
 * In-place navigation for a surface that cannot reach the router through its hooks.
 *
 * Most links do reach it: the rail and a screen's own navigation are `<Link>`s. Two kinds cannot.
 * The sidebar is drawn on every screen from a module's `sidebarSections` slot, and the dialogs it
 * opens live on whichever screen the reader happens to be on; and the sign-in screens are mounted
 * bare by their own tests, where a `<Link>` has no router to ask. So the root of the application
 * installs one function here for as long as it is mounted, and those surfaces call it instead of
 * handing an address to the browser — which would throw the document away, boot the client again
 * and dial a second socket for a move between two screens of the same application.
 *
 * Without an installed function every link is still a working `<a href>`: a full page load rather
 * than a dead control, which is the right failure for a navigation.
 */

import type { MouseEvent } from "react"

let navigateFn: ((path: string) => void) | null = null

/** Installed by the application's root route while it is mounted; cleared when it is not. */
export function setNavigate(fn: ((path: string) => void) | null): void {
	navigateFn = fn
}

/** Navigates in place and answers whether it happened; false leaves the anchor to the browser. */
export function navigateTo(path: string): boolean {
	if (!navigateFn) return false
	navigateFn(path)
	return true
}

/**
 * The way out of a screen on a window that shows one thing at a time: a conversation's back arrow,
 * and an AI chat's (VISUAL.md §7.3).
 *
 * Going back is right whenever there is something to go back to, because on that width the only way
 * in was the list. Whether there is, is read from the history entry the router is on: it counts its
 * own entries in `history.state`, so an index above zero was pushed by this application and stepping
 * back lands inside it. The first entry of a session — a pasted link, a reload, a notification — has
 * nothing behind it, and the list the caller names is the honest answer then; so it is where the
 * counter is not there at all, which is why this reads it defensively rather than trusting it.
 */
export function goBackOr(list: string): void {
	const index = (window.history.state as { __TSR_index?: number } | null)?.__TSR_index
	if (typeof index === "number" && index > 0) window.history.back()
	else if (!navigateTo(list)) window.location.assign(list)
}

/**
 * The click handler of an `<a href>` that should stay in this document. It prevents the browser's
 * default only when the navigation actually happened here, and never for a modified click — which
 * is how a person opens a link in a new tab, and taking that away is worse than a page load.
 */
export function inPlaceClick(path: string): (event: MouseEvent<HTMLAnchorElement>) => void {
	return (event) => {
		if (event.metaKey || event.ctrlKey || event.shiftKey) return
		if (navigateTo(path)) event.preventDefault()
	}
}
